// ZUSTAND→ATOM off-ramp (docs/projects/complete/web/zustand-to-effect-atom-migration.md)
// Status: converted
// atomKvsSync persisted atom; zustand `{state, version}` envelopes (with
// possibly-partial state from older app versions) migrate forward through the
// schema transform below.
import { RegistryProvider, useAtomSet, useAtomValue } from '@effect/atom-react';
import { Schema, SchemaTransformation, Struct } from 'effect';
import type { AtomRegistry } from 'effect/unstable/reactivity';
import { type PropsWithChildren, useMemo } from 'react';
import type { AudioItemSummary } from '@listening/api-contract/legacy-client-types';
import { atomKvsSync, fieldwiseKvsFallback } from '../atoms/kvs';

const fontStyles = [
  'Poppins',
  'Besley',
  'Roboto Serif',
  'Playfair Display',
  'Quicksand',
  'OpenDyslexic',
] as const;

const fontSizes = ['Small', 'Medium', 'Large'] as const;

const fontWeights = ['Regular', 'Medium', 'Semibold'] as const;

const lineHeights = ['Compact', 'Comfort', 'Spacious'] as const;

type FontSettings = {
  size: (typeof fontSizes)[number];
  style: (typeof fontStyles)[number];
  weight: (typeof fontWeights)[number];
  lineHeight: (typeof lineHeights)[number];
};

const fontStyleLabelToClassName: Record<(typeof fontStyles)[number], string> = {
  Poppins: "font-['Poppins']",
  Besley: "font-['Besley_Variable']",
  'Roboto Serif': "font-['Roboto_Serif_Variable']",
  'Playfair Display': "font-['Playfair_Display_Variable']",
  Quicksand: "font-['Quicksand_Variable']",
  OpenDyslexic: "font-['OpenDyslexic']",
};

const fontSizeLabelToClassName: Record<(typeof fontSizes)[number], string> = {
  Small: 'text-base',
  Medium: 'text-lg',
  Large: 'text-xl',
};

const fontWeightLabelToValue = {
  Regular: '400',
  Medium: '500',
  Semibold: '600',
} as const satisfies Record<
  (typeof fontWeights)[number],
  '400' | '500' | '600'
>;

const lineHeightLabelToMultiplier: Record<
  (typeof lineHeights)[number],
  number
> = {
  Compact: 1.65,
  Comfort: 1.8,
  Spacious: 2,
};

export const readalongFontConfig = {
  fontStyles,
  fontSizes,
  fontWeights,
  lineHeights,
  fontStyleLabelToClassName,
  fontSizeLabelToClassName,
  fontWeightLabelToValue,
  lineHeightLabelToMultiplier,
};

const docFontOptions = [
  {
    key: 'inter',
    label: 'Inter',
    className: "font-['Inter_Variable',sans-serif]",
  },
  {
    key: 'lexend',
    label: 'Lexend',
    className: "font-['Lexend_Variable',sans-serif]",
  },
  {
    key: 'opendyslexic',
    label: 'OpenDyslexic',
    className: "font-['OpenDyslexic',sans-serif]",
  },
  {
    key: 'atkinson',
    label: 'Atkinson Hyperlegible',
    className: "font-['Atkinson_Hyperlegible_Next_Variable',sans-serif]",
  },
  {
    key: 'helvetica',
    label: 'Helvetica',
    className: "font-['Helvetica','Arial',sans-serif]",
  },
  { key: 'arial', label: 'Arial', className: "font-['Arial',sans-serif]" },
  { key: 'system', label: 'System', className: 'font-sans' },
] as const;

const docFontWeightOptions = [
  { key: 'regular', label: 'Regular', className: 'font-normal' },
  { key: 'medium', label: 'Medium', className: 'font-medium' },
  { key: 'semibold', label: 'Semibold', className: 'font-semibold' },
] as const;

const docTextSizeOptions = [
  { key: 'small', label: 'Small', className: 'text-[15px]' },
  { key: 'medium', label: 'Medium', className: 'text-[17px]' },
  { key: 'large', label: 'Large', className: 'text-[19px]' },
] as const;

const docLineHeightOptions = [
  { key: 'compact', label: 'Compact', className: 'leading-[1.65]' },
  { key: 'comfortable', label: 'Comfort', className: 'leading-[1.8]' },
  { key: 'spacious', label: 'Spacious', className: 'leading-[2]' },
] as const;

const docContentWidthOptions = [
  { key: 'narrow', label: 'Narrow', className: 'max-w-3xl' },
  { key: 'standard', label: 'Standard', className: 'max-w-4xl' },
  { key: 'wide', label: 'Wide', className: 'max-w-5xl' },
] as const;

const docParagraphSpacingOptions = [
  { key: 'tight', label: 'Tight', className: 'py-2' },
  { key: 'normal', label: 'Normal', className: 'py-3' },
  { key: 'relaxed', label: 'Relaxed', className: 'py-4' },
] as const;

const docActiveEmphasisOptions = [
  { key: 'color', label: 'Color' },
  { key: 'highlight', label: 'Highlight' },
  { key: 'underline', label: 'Underline' },
] as const;

type DocFontKey = (typeof docFontOptions)[number]['key'];
type DocFontWeightKey = (typeof docFontWeightOptions)[number]['key'];
type DocTextSizeKey = (typeof docTextSizeOptions)[number]['key'];
type DocLineHeightKey = (typeof docLineHeightOptions)[number]['key'];
type DocContentWidthKey = (typeof docContentWidthOptions)[number]['key'];
type DocParagraphSpacingKey =
  (typeof docParagraphSpacingOptions)[number]['key'];
type DocActiveEmphasisKey = (typeof docActiveEmphasisOptions)[number]['key'];

export {
  docFontOptions,
  docFontWeightOptions,
  docTextSizeOptions,
  docLineHeightOptions,
  docContentWidthOptions,
  docParagraphSpacingOptions,
  docActiveEmphasisOptions,
  type DocFontKey,
  type DocFontWeightKey,
  type DocTextSizeKey,
  type DocLineHeightKey,
  type DocContentWidthKey,
  type DocParagraphSpacingKey,
  type DocActiveEmphasisKey,
};

const fontSettingsSchema = Schema.Struct({
  size: Schema.Literals(fontSizes),
  style: Schema.Literals(fontStyles),
  weight: Schema.Literals(fontWeights),
  lineHeight: Schema.Literals(lineHeights),
});

export const audioPrefsStateSchema = Schema.Struct({
  playbackRate: Schema.Finite,
  _volume: Schema.Finite,
  muted: Schema.Boolean,
  fontSettings: fontSettingsSchema,
  readAlongViewer: Schema.Literals(['normal', 'pdf']),
  pdfScale: Schema.Finite,
  autoScroll: Schema.Boolean,
  docFont: Schema.Literals(docFontOptions.map((o) => o.key)),
  docFontWeight: Schema.Literals(docFontWeightOptions.map((o) => o.key)),
  docTextSize: Schema.Literals(docTextSizeOptions.map((o) => o.key)),
  docLineHeight: Schema.Literals(docLineHeightOptions.map((o) => o.key)),
  docContentWidth: Schema.Literals(docContentWidthOptions.map((o) => o.key)),
  docParagraphSpacing: Schema.Literals(
    docParagraphSpacingOptions.map((o) => o.key),
  ),
  docActiveEmphasis: Schema.Literals(
    docActiveEmphasisOptions.map((o) => o.key),
  ),
  docFullscreen: Schema.Boolean,
});

export type AudioPrefsState = typeof audioPrefsStateSchema.Type;

export const defaultAudioPrefs: AudioPrefsState = {
  playbackRate: 1,
  _volume: 1,
  muted: false,
  fontSettings: {
    size: 'Medium',
    style: 'Poppins',
    weight: 'Regular',
    lineHeight: 'Comfort',
  },
  readAlongViewer: 'pdf',
  pdfScale: 1,
  autoScroll: false,
  docFont: 'inter',
  docFontWeight: 'regular',
  docTextSize: 'medium',
  docLineHeight: 'comfortable',
  docContentWidth: 'standard',
  docParagraphSpacing: 'normal',
  docActiveEmphasis: 'color',
  docFullscreen: false,
};

// zustand-persist wrote a `{state, version}` envelope under the same key, and
// `state` can be PARTIAL — users whose last write predates a field simply
// don't have it (zustand merged persisted-over-defaults on rehydrate). The
// transform is the migration (effect-atom has no version/migrate hook):
// decode accepts envelope or bare, partial or full, and merges over the
// defaults; encode writes the full bare shape (the envelope branch becomes
// dead a release after every user has written once). See plan doc §C2.
// `mapFields(Struct.map(Schema.optional))` is SHALLOW: it makes each top-level
// field optional, but a nested struct that IS present must still match in full.
// So `fontSettings` gets its own partial — otherwise adding a field to it (as
// `weight` and `lineHeight` were) rejects every value already in storage, and
// the merge below never runs.
const partialAudioPrefsSchema = Schema.Struct({
  ...audioPrefsStateSchema.fields,
  fontSettings: fontSettingsSchema.mapFields(Struct.map(Schema.optional)),
}).mapFields(Struct.map(Schema.optional));
// The bare branch must not match an ENVELOPE. Every field is optional and
// `Schema.Struct` ignores excess keys, so without this a `{state, version}`
// object whose `state` fails validation matches here vacuously — decoding to
// `{}`, i.e. every preference silently reset — instead of failing and letting
// `fieldFallback` recover the still-valid fields.
const bareAudioPrefsSchema = Schema.Struct({
  ...partialAudioPrefsSchema.fields,
  state: Schema.optional(Schema.Never),
});
const persistedAudioPrefsSchema = Schema.Union([
  Schema.Struct({
    state: partialAudioPrefsSchema,
    version: Schema.Int,
  }),
  bareAudioPrefsSchema,
]).pipe(
  Schema.decodeTo(
    audioPrefsStateSchema,
    SchemaTransformation.transform({
      decode: (stored) => {
        // Discriminate on `version`: the bare branch declares `state` (as
        // `never`) to reject envelopes, so `'state' in stored` no longer
        // narrows the union.
        const persisted = 'version' in stored ? stored.state : stored;
        return {
          ...defaultAudioPrefs,
          ...persisted,
          fontSettings: {
            ...defaultAudioPrefs.fontSettings,
            ...persisted.fontSettings,
          },
        };
      },
      encode: (state) => state,
    }),
  ),
);

export const AUDIO_PREFS_STORAGE_KEY = 'audio-prefs-storage';

export const audioPreferencesAtom = atomKvsSync({
  key: AUDIO_PREFS_STORAGE_KEY,
  schema: persistedAudioPrefsSchema,
  defaultValue: () => defaultAudioPrefs,
  // When a whole-blob decode fails (e.g. a retired `docFont` literal after an
  // app update), recover field-wise: keep every still-valid top-level
  // preference and default only the invalid one, instead of wiping the whole
  // object. Recovery is top-level only, so a bad NESTED value (e.g. a retired
  // `fontSettings.style`) defaults its whole top-level field (`fontSettings`).
  fieldFallback: fieldwiseKvsFallback({
    schema: audioPrefsStateSchema,
    defaultValue: () => defaultAudioPrefs,
  }),
});

export const resolveAudioVolume = (
  state: Pick<AudioPrefsState, 'muted' | '_volume'>,
): number => (state.muted ? 0 : state._volume);

type AudioPrefsActions = {
  setPlaybackRate: (playbackRate: number) => void;
  getVolume: () => number;
  setVolume: (volume: number) => void;
  setMuted: (muted: boolean) => void;
  setFontSettings: (settings: FontSettings) => void;
  reset: () => void;
  setReadAlongViewer: (viewer: 'normal' | 'pdf') => void;
  setPdfScale: (scale: number) => void;
  setAutoScroll: (autoScroll: boolean) => void;
  setDocFont: (font: DocFontKey) => void;
  setDocFontWeight: (weight: DocFontWeightKey) => void;
  setDocTextSize: (size: DocTextSizeKey) => void;
  setDocLineHeight: (height: DocLineHeightKey) => void;
  setDocContentWidth: (width: DocContentWidthKey) => void;
  setDocParagraphSpacing: (spacing: DocParagraphSpacingKey) => void;
  setDocActiveEmphasis: (emphasis: DocActiveEmphasisKey) => void;
  setDocFullscreen: (fullscreen: boolean) => void;
};

type AudioPrefsStore = AudioPrefsState & AudioPrefsActions;

function useAudioPrefsActions(): Omit<AudioPrefsActions, 'getVolume'> {
  const setPrefs = useAtomSet(audioPreferencesAtom);
  return useMemo(
    () => ({
      setPlaybackRate: (playbackRate: number) => {
        setPrefs((s) => ({ ...s, playbackRate }));
      },
      setVolume: (volume: number) => {
        setPrefs((s) => ({ ...s, _volume: volume, muted: volume === 0 }));
      },
      setMuted: (muted: boolean) => {
        setPrefs((s) => ({ ...s, muted }));
      },
      setFontSettings: (fontSettings: FontSettings) => {
        setPrefs((s) => ({ ...s, fontSettings }));
      },
      reset: () => {
        setPrefs(defaultAudioPrefs);
      },
      setReadAlongViewer: (readAlongViewer: 'normal' | 'pdf') => {
        setPrefs((s) => ({ ...s, readAlongViewer }));
      },
      setPdfScale: (scale: number) => {
        if (scale < 0.2) return;
        if (scale > 5) return;
        setPrefs((s) => ({ ...s, pdfScale: scale }));
      },
      setAutoScroll: (autoScroll: boolean) => {
        setPrefs((s) => ({ ...s, autoScroll }));
      },
      setDocFont: (docFont: DocFontKey) => {
        setPrefs((s) => ({ ...s, docFont }));
      },
      setDocFontWeight: (docFontWeight: DocFontWeightKey) => {
        setPrefs((s) => ({ ...s, docFontWeight }));
      },
      setDocTextSize: (docTextSize: DocTextSizeKey) => {
        setPrefs((s) => ({ ...s, docTextSize }));
      },
      setDocLineHeight: (docLineHeight: DocLineHeightKey) => {
        setPrefs((s) => ({ ...s, docLineHeight }));
      },
      setDocContentWidth: (docContentWidth: DocContentWidthKey) => {
        setPrefs((s) => ({ ...s, docContentWidth }));
      },
      setDocParagraphSpacing: (docParagraphSpacing: DocParagraphSpacingKey) => {
        setPrefs((s) => ({ ...s, docParagraphSpacing }));
      },
      setDocActiveEmphasis: (docActiveEmphasis: DocActiveEmphasisKey) => {
        setPrefs((s) => ({ ...s, docActiveEmphasis }));
      },
      setDocFullscreen: (docFullscreen: boolean) => {
        setPrefs((s) => ({ ...s, docFullscreen }));
      },
    }),
    [setPrefs],
  );
}

/**
 * Test-only isolation wrapper (browser specs): forks an atom registry for the
 * subtree, seeded with `initProps` over the defaults. App trees use the root
 * registry and never mount this.
 */
export function AudioPrefsStoreProvider({
  children,
  initProps,
}: { initProps?: Partial<AudioPrefsState> } & PropsWithChildren) {
  return (
    <RegistryProvider
      initialValues={[
        [audioPreferencesAtom, { ...defaultAudioPrefs, ...initProps }],
      ]}
    >
      {children}
    </RegistryProvider>
  );
}

/**
 * Selector-style read over the audio-preferences atom, kept signature
 * compatible with the zustand store it replaced. Subscribes to the whole
 * preferences object (it changes rarely); the actions in the view are
 * referentially stable.
 */
export function useAudioPrefsStore<T>(
  selector: (state: AudioPrefsStore) => T,
): T {
  const actions = useAudioPrefsActions();
  // Selector routed through a derived atom: notifications are gated on the
  // SELECTED value (zustand parity) — selecting one preference doesn't
  // rerender on unrelated preference writes; selecting an action never
  // rerenders.
  return useAtomValue(audioPreferencesAtom, (state) =>
    selector({
      ...state,
      ...actions,
      getVolume: () => resolveAudioVolume(state),
    }),
  );
}

/**
 * Imperative reset for non-React callers (mobile user teardown). Writing
 * through the atom also persists the reset state. `overrides` lets mobile
 * keep its platform default (autoScroll on).
 */
export const resetAudioPreferences = (
  registry: AtomRegistry.AtomRegistry,
  overrides?: Partial<AudioPrefsState>,
): void => {
  registry.set(audioPreferencesAtom, { ...defaultAudioPrefs, ...overrides });
};

export function getDocFontClassName(key: DocFontKey) {
  return docFontOptions.find((o) => o.key === key)?.className ?? '';
}

export function getDocFontWeightClassName(key: DocFontWeightKey) {
  return docFontWeightOptions.find((o) => o.key === key)?.className ?? '';
}

export function getDocTextSizeClassName(key: DocTextSizeKey) {
  return docTextSizeOptions.find((o) => o.key === key)?.className ?? '';
}

export function getDocLineHeightClassName(key: DocLineHeightKey) {
  return docLineHeightOptions.find((o) => o.key === key)?.className ?? '';
}

export function getDocContentWidthClassName(key: DocContentWidthKey) {
  return docContentWidthOptions.find((o) => o.key === key)?.className ?? '';
}

export function getDocParagraphSpacingClassName(key: DocParagraphSpacingKey) {
  return docParagraphSpacingOptions.find((o) => o.key === key)?.className ?? '';
}

export function isReadAlongPdfAvailable(audioItem?: AudioItemSummary):
  | {
      isPdf: true;
      documentUrl: string;
    }
  | {
      isPdf: false;
      documentUrl: null;
    } {
  const documentUrl = audioItem?.document_url;
  if (!documentUrl) return { isPdf: false, documentUrl: null };
  if (audioItem.document_type === 'TXT') {
    return { isPdf: false, documentUrl: null };
  }

  const parsed = new URL(documentUrl);
  const isPdf = parsed.pathname.endsWith('.pdf');
  if (!isPdf) return { isPdf: false, documentUrl: null };

  return {
    isPdf: true,
    documentUrl,
  };
}

export function useReadAlongMainContentView(
  audioItem: AudioItemSummary | null,
):
  | {
      viewMode: 'pdf';
      documentUrl: string;
    }
  | {
      viewMode: 'normal';
      documentUrl: string | null;
    } {
  const readAlongViewerPreference = useAudioPrefsStore(
    (state) => state.readAlongViewer,
  );
  const { isPdf, documentUrl } = isReadAlongPdfAvailable(
    audioItem ?? undefined,
  );

  if (isPdf && readAlongViewerPreference === 'pdf') {
    return {
      viewMode: 'pdf',
      documentUrl,
    };
  }
  return {
    viewMode: 'normal',
    documentUrl,
  };
}
